import { ReactNode, useRef } from "react";
import { Icon } from "src/components";
import { Css, Palette } from "src/Css";
import { useTestIds } from "src/hooks";

export interface CheckboxProps<T> {
  children: ReactNode;
  onChange: (value: T) => void;
  value: string;
  checked?: boolean;
}

/** Checkbox component with keyboard accessibility support */
export function Checkbox<T extends string = string>(props: CheckboxProps<T>) {
  const { children, checked, onChange, value } = props;
  const [, checkboxId, inputId] = useTestIds("checkbox", [value, "input"]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  function handleOnChange() {
    // Remove input outline styles when clicking on the checkmark
    wrapperRef?.current?.blur();
    onChange(value as T);
  }

  /** Triggers onChange event when pressing the space key */
  function handleKeyPress(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === " ") onChange(value as T);
  }

  return (
    <div ref={wrapperRef} css={Css.onActive.outline0.$} tabIndex={0} onKeyPress={handleKeyPress}>
      <input
        css={Css.dn.$}
        type="checkbox"
        onChange={handleOnChange}
        id={value}
        value={value}
        checked={checked}
        tabIndex={-1}
        {...inputId}
      />
      <label htmlFor={value} css={Css.t14.blue.df.aic.cursorPointer.$} {...checkboxId}>
        <>
          {checked ? (
            <Icon icon="checkboxOutlineFill" color={Palette.Blue600} xss={Css.mr1.sqPx(18).mwPx(18).$} />
          ) : (
            <Icon icon="checkboxOutline" color={Palette.Black} xss={Css.mr1.sqPx(18).mwPx(18).$} />
          )}
          {children}
        </>
      </label>
    </div>
  );
}
